import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/contexts/SidebarContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: string;
  icon?: ReactNode;
  backTo?: string;
  actions?: ReactNode;
  subtitle?: ReactNode;
}

export function PageHeader({ title, icon, backTo, actions, subtitle }: PageHeaderProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Try to use sidebar context, but don't fail if not in provider
  let collapsed = false;
  try {
    const sidebar = useSidebar();
    collapsed = sidebar.collapsed;
  } catch {
    // Not in sidebar provider context
  }

  // On mobile, add left padding for the hamburger menu button
  // On desktop, we use sticky positioning which stays within the content area (already offset by AppLayout margin)
  const mobilePadding = isMobile ? "pl-16" : "";

  return (
    <header
      className={cn(
        "bg-card border-b border-border sticky top-0 z-30 transition-all duration-200",
        mobilePadding
      )}
    >
      <div className="flex items-center gap-3 h-14 px-3 sm:px-4">
        {backTo && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(backTo)}
            className="h-8 w-8 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        {icon && <div className="shrink-0 text-muted-foreground">{icon}</div>}
        <div className="flex-1 min-w-0">
          <h1 className="text-base sm:text-lg font-semibold text-foreground truncate">{title}</h1>
          {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    </header>
  );
}
